import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ExternalLink, PersonStanding, X } from 'lucide-react';
import type { GeoPoint } from '@uniticket/shared';
import { cn, streetViewUrl } from '@/lib/utils';

// Street View intégré (demande user du 2026-10-16) : le viewer partagé d'Unifiber, servi par internal.unifiber.be,
// dans une iframe. La clé Google reste chez lui ; l'app ne lui passe qu'un point. Guide :
// internal-apps-it/streetview/README.md.
//
// Ce que le guide impose, et qui explique ce fichier :
//  • le viewer n'accepte d'être cadré que depuis une page https://*.unifiber.be (en local, le cadre est refusé), et
//    la page doit l'autoriser dans sa CSP (`frame-src`, posée dans docker/nginx.conf) ;
//  • il ne répond qu'aux comptes des groupes Entra Unifiber_apps_internal / Unifiber_apps_admin, depuis le réseau du
//    bureau ; une session expirée, il le dit lui-même dans le cadre ;
//  • chaque ouverture coûte ~35 à 80 requêtes sur un quota Google commun à toutes les apps (2 900 par jour). D'où
//    l'iframe créée au clic et détruite à la fermeture, et le lien Google gratuit gardé en secours dans l'en-tête.
//
// Panneau FLOTTANT plutôt que modale (choix du 2026-10-16, celui du guide, qui renvoie au panneau d'unicheck) : on
// regarde la façade PENDANT qu'on remplit la fiche, comme le volet « commentaire du ticket », non modal lui aussi.
// Déplacé par son en-tête, redimensionné par son coin, fermé par la croix ou Échap. Échap n'arrive plus à la page une
// fois qu'on a cliqué dans l'image : le clavier appartient alors au cadre, qui est un autre site.
const STREETVIEW_ORIGIN = 'https://internal.unifiber.be';

export function streetViewerUrl(p: GeoPoint): string {
  return `${STREETVIEW_ORIGIN}/streetview/?lat=${p.lat.toFixed(6)}&lng=${p.lon.toFixed(6)}`;
}

const MIN_WIDTH = 360;
const MIN_HEIGHT = 240;
const MARGIN = 12;

// Où le panneau a été posé et à quelle taille, gardés le temps de la page : le rouvrir ne le renvoie pas à sa place
// d'origine.
let lastPosition: { left: number; top: number } | null = null;
let lastSize: { width: number; height: number } | null = null;

const clamp = (v: number, min: number, max: number) => Math.min(Math.max(v, min), Math.max(min, max));

/** L'état ouvert/fermé du panneau, qui se referme de lui-même quand le point change (ticket suivant, autre adresse
 *  dans la console) : un nouveau point ne doit rien charger sans un nouveau clic. */
export function useStreetView(point: GeoPoint | null) {
  const [open, setOpen] = useState(false);
  const key = point ? `${point.lat.toFixed(6)},${point.lon.toFixed(6)}` : null;
  useEffect(() => { setOpen(false); }, [key]);
  const show = useCallback(() => setOpen(true), []);
  const close = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => setOpen((o) => !o), []);
  return { open: open && point != null, show, toggle, close };
}

/** Le bonhomme. Grisé, avec « Coordonnées manquantes » au survol, quand l'adresse n'a pas de point plausible. */
export function StreetViewIconButton({ point, active, onClick, className }: {
  point: GeoPoint | null; active?: boolean; onClick: () => void; className?: string;
}) {
  const { t } = useTranslation(['tickets']);
  const label = point ? t('tickets:streetViewPanel.open') : t('tickets:streetViewPanel.noCoords');
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!point}
      title={label}
      aria-label={label}
      aria-pressed={point ? !!active : undefined}
      className={cn(
        'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border transition-colors',
        active
          ? 'border-amber-300 bg-amber-50 text-amber-600'
          : 'border-slate-200 text-amber-500 hover:border-amber-300 hover:bg-amber-50',
        'disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-transparent disabled:text-slate-300',
        className,
      )}
    >
      <PersonStanding className="h-4 w-4" />
    </button>
  );
}

/** Le bonhomme et son panneau d'un bloc, pour un emplacement qui n'a rien d'autre à brancher dessus. */
export function StreetViewButton({ point, label, className }: { point: GeoPoint | null; label?: string | null; className?: string }) {
  const sv = useStreetView(point);
  return (
    <>
      <StreetViewIconButton point={point} active={sv.open} onClick={sv.toggle} className={className} />
      {sv.open && point && <StreetViewPanel point={point} label={label} onClose={sv.close} />}
    </>
  );
}

export function StreetViewPanel({ point, label, onClose }: { point: GeoPoint; label?: string | null; onClose: () => void }) {
  const { t } = useTranslation(['tickets']);
  const src = streetViewerUrl(point);
  const panelRef = useRef<HTMLDivElement>(null);
  const gesture = useRef<{ kind: 'drag' | 'resize'; x: number; y: number; rect: DOMRect } | null>(null);
  const [position, setPosition] = useState(lastPosition);
  const [size, setSize] = useState(lastSize);
  const [busy, setBusy] = useState(false);

  // Échap ferme le panneau, et lui seul. Écouté en CAPTURE sur window, donc avant Radix : dans un tiroir (fiche
  // d'appartement, console ouverte depuis un ticket), Radix écoute Échap en capture sur document et fermerait le
  // tiroir entier avec. On laisse la main à une boîte de dialogue modale qui a le focus (ConfirmDialog, DetailModal…).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const target = e.target instanceof Element ? e.target : null;
      if (target?.closest('[aria-modal="true"]') && !panelRef.current?.contains(target)) return;
      e.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  // Garder le panneau dans la fenêtre : à l'ouverture (la place retenue vient peut-être d'une fenêtre plus grande),
  // puis à chaque redimensionnement de la fenêtre.
  useLayoutEffect(() => {
    const fit = () => {
      const el = panelRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setSize((s) => s && {
        width: clamp(s.width, MIN_WIDTH, window.innerWidth - 2 * MARGIN),
        height: clamp(s.height, MIN_HEIGHT, window.innerHeight - 2 * MARGIN),
      });
      setPosition((p) => p && {
        left: clamp(p.left, 0, window.innerWidth - Math.min(r.width, window.innerWidth)),
        top: clamp(p.top, 0, window.innerHeight - Math.min(r.height, window.innerHeight)),
      });
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, []);

  // Les deux gestes épinglent d'abord le panneau par son coin haut-gauche, en pixels.
  const begin = (kind: 'drag' | 'resize') => (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0 || !panelRef.current) return;
    if (kind === 'drag' && e.target instanceof Element && e.target.closest('a, button')) return;
    const rect = panelRef.current.getBoundingClientRect();
    setPosition({ left: rect.left, top: rect.top });
    gesture.current = { kind, x: e.clientX, y: e.clientY, rect };
    e.currentTarget.setPointerCapture(e.pointerId);
    setBusy(true);
    e.preventDefault();
  };

  const move = (e: ReactPointerEvent<HTMLDivElement>) => {
    const g = gesture.current;
    if (!g) return;
    const dx = e.clientX - g.x;
    const dy = e.clientY - g.y;
    if (g.kind === 'drag') {
      const next = {
        left: clamp(g.rect.left + dx, 0, window.innerWidth - g.rect.width),
        top: clamp(g.rect.top + dy, 0, window.innerHeight - g.rect.height),
      };
      lastPosition = next;
      setPosition(next);
    } else {
      const next = {
        width: clamp(g.rect.width + dx, MIN_WIDTH, window.innerWidth - g.rect.left),
        height: clamp(g.rect.height + dy, MIN_HEIGHT, window.innerHeight - g.rect.top),
      };
      lastPosition = { left: g.rect.left, top: g.rect.top };
      lastSize = next;
      setSize(next);
    }
  };

  const end = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!gesture.current) return;
    gesture.current = null;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    setBusy(false);
  };

  const handlers = (kind: 'drag' | 'resize') => ({
    onPointerDown: begin(kind), onPointerMove: move, onPointerUp: end, onPointerCancel: end,
  });

  // Par défaut en haut à gauche : à côté des tiroirs, qui s'ouvrent à droite (même place que le volet commentaire).
  const style: CSSProperties = {
    left: position?.left ?? MARGIN,
    top: position?.top ?? MARGIN,
    width: size?.width ?? `min(936px, calc(100vw - ${2 * MARGIN}px))`,
    height: size?.height ?? `min(600px, calc(100vh - ${2 * MARGIN}px))`,
  };

  // Portail sur <body> : une carte en verre (backdrop-filter) ferait de son cadre le repère du `fixed`. Et
  // `pointer-events-auto` + `z-[60]`, comme DetailModal : un tiroir Radix ouvert pose `pointer-events: none` sur
  // <body>, et un portail hors de son contenu en hérite (défaut du 14-09).
  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-label={t('tickets:streetViewPanel.title')}
      style={style}
      className="pointer-events-auto fixed z-[60] flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl"
    >
      <div
        {...handlers('drag')}
        title={t('tickets:streetViewPanel.drag')}
        className="flex shrink-0 cursor-move select-none items-center gap-2 border-b border-slate-100 px-3 py-2"
      >
        <PersonStanding className="h-4 w-4 shrink-0 text-amber-500" aria-hidden="true" />
        <h3 className="shrink-0 text-sm font-bold text-slate-800">{t('tickets:streetViewPanel.title')}</h3>
        {label && <span className="min-w-0 truncate text-xs text-slate-500">{label}</span>}
        <a
          href={streetViewUrl(point.lat, point.lon)}
          target="_blank"
          rel="noopener noreferrer"
          title={t('tickets:streetViewPanel.googleHint')}
          className="ml-auto inline-flex shrink-0 items-center gap-1 text-[11px] font-medium text-brand-rose hover:underline"
        >
          {t('tickets:streetViewPanel.google')}
          <ExternalLink className="h-3 w-3" aria-hidden="true" />
        </a>
        <button
          type="button"
          onClick={onClose}
          title={t('tickets:streetViewPanel.close')}
          aria-label={t('tickets:streetViewPanel.close')}
          className="shrink-0 rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      {/* Pendant un geste, le cadre ne doit pas avaler le pointeur qui passe au-dessus de lui. */}
      <iframe
        key={src}
        src={src}
        title={t('tickets:streetViewPanel.title')}
        allow="fullscreen"
        className={cn('min-h-0 w-full flex-1 border-0 bg-slate-100', busy && 'pointer-events-none')}
      />
      {/* La poignée : 20 px de prise, la marque en retrait. Le coin arrondi du panneau rogne l'extrême coin (constaté
          sur le banc le 2026-10-19 : un triangle dessiné dans l'angle même ne recevait pas le clic). */}
      <div
        {...handlers('resize')}
        title={t('tickets:streetViewPanel.resize')}
        className="absolute bottom-0 right-0 flex h-5 w-5 cursor-se-resize items-end justify-end p-1"
      >
        <span className="block h-2.5 w-2.5 border-b-2 border-r-2 border-slate-400" aria-hidden="true" />
      </div>
    </div>,
    document.body,
  );
}
